(() => {
  'use strict';
  const root = document.documentElement;
  const storageKey = 'ak-colour-theme-v1';
  const buttons = [];
  // A single device-pixel conversion for hatch and grid construction. Layout
  // fractions remain shared with native CSS grids; never round each field's
  // width and then add the rounded widths (that accumulates seam errors).
  const pixels = window.AKPixels = Object.freeze({
    ratio: () => window.devicePixelRatio || 1,
    snap: value => Math.round(value * (window.devicePixelRatio || 1)) / (window.devicePixelRatio || 1),
    local: (origin, offset) => (Math.round((origin + offset) * (window.devicePixelRatio || 1)) / (window.devicePixelRatio || 1)) - origin
  });
  const themes = ['light', 'brutal', 'third'];
  const validTheme = theme => themes.includes(theme) ? theme : 'light';
  const readSaved = () => {
    try { return validTheme(localStorage.getItem(storageKey)); }
    catch (_) { return validTheme(root.dataset.akTheme); }
  };
  const nextTheme = () => {
    const current = validTheme(root.dataset.akTheme);
    return themes[(themes.indexOf(current) + 1) % themes.length];
  };
  const syncButtons = () => {
    buttons.forEach(button => {
      const target = nextTheme();
      const label = `Switch to ${target[0].toUpperCase() + target.slice(1)} theme`;
      button.setAttribute('aria-label', label);
      const active = root.dataset.akTheme !== 'light';
      button.setAttribute('aria-pressed', String(active));
      button.title = label;
    });
    if (document.readyState !== 'loading') {
      const meta = document.querySelector('meta[name="theme-color"]');
      const wall = getComputedStyle(root).getPropertyValue('--ak-wall').trim();
      if (meta && wall) meta.setAttribute('content', wall);
    }
  };
  // A cached, static PNG of random 1.5px flecks in the current theme colour.
  // The fixed seed preserves its arrangement after reload or a theme change.
  const grainTiles = new Map();
  const grainSpan = 128;
  const grainSize = () => Math.max(128, Math.round(grainSpan * pixels.ratio()));
  const grainPatternFor = colour => {
    const size = grainSize();
    const key = `${colour}|${size}`;
    if (grainTiles.has(key)) return grainTiles.get(key);
    let pattern = 'none';
    try {
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = size;
      const context = canvas.getContext('2d');
      if (!context || !colour) return pattern;
      const scale = size / grainSpan;
      context.scale(scale, scale);
      context.fillStyle = colour;
      let seed = 172903;
      const random = () => {
        seed ^= seed << 13; seed ^= seed >>> 17; seed ^= seed << 5;
        return (seed >>> 0) / 4294967296;
      };
      // About one 1.5px dot per 20 square CSS pixels: visible flecks with
      // plenty of untouched surface, rather than an overall noise tint.
      const count = Math.round(grainSpan * grainSpan / 20);
      for (let index = 0; index < count; index += 1) {
        const x = Math.round(random() * size) / scale;
        const y = Math.round(random() * size) / scale;
        context.globalAlpha = .75 + random() * .25;
        for (const offsetX of [-grainSpan, 0, grainSpan]) {
          for (const offsetY of [-grainSpan, 0, grainSpan]) {
            const cx = x + offsetX;
            const cy = y + offsetY;
            if (cx < -.75 || cx > grainSpan + .75 || cy < -.75 || cy > grainSpan + .75) continue;
            context.beginPath();
            context.arc(cx, cy, .75, 0, Math.PI * 2);
            context.fill();
          }
        }
      }
      pattern = `url("${canvas.toDataURL('image/png')}")`;
      if (grainTiles.size >= 12) grainTiles.delete(grainTiles.keys().next().value);
      grainTiles.set(key, pattern);
    } catch (_) { /* Keep the existing field surface if canvas is unavailable. */ }
    return pattern;
  };
  const syncGrainPattern = () => {
    const style = getComputedStyle(root);
    const pattern = grainPatternFor(style.getPropertyValue('--ak-hatch').trim());
    const railPattern = root.dataset.akTheme === 'third'
      ? grainPatternFor(style.getPropertyValue('--th-texti').trim()) : pattern;
    for (const [name, value] of [['--ak-hatch-pattern', pattern], ['--ak-rail-hatch-pattern', railPattern]]) {
      if (root.style.getPropertyValue(name) !== value) root.style.setProperty(name, value);
    }
    const repeat = grainSize() / pixels.ratio();
    root.style.setProperty('--ak-hatch-size', `${repeat}px ${repeat}px`);
  };
  // Corner logos are CSS background images, so tint their SVG source without
  // filtering the box's background, borders or original mobile dimensions.
  const syncForegroundCorners = () => {
    if (!document.body) return;
    const colour = getComputedStyle(root).getPropertyValue('--ak-texti').trim();
    if (!colour) return;
    const scale = matchMedia('(max-width: 1200px)').matches ? '.0493823675' : '.0548692972';
    const path = 'M462.495000 219.889374L323.632122 219.889374L421.821587 121.699910L378.815090 78.693413L280.625626 176.882878L280.625626 38.020000L219.804374 38.020000L219.804374 176.882878L121.614910 78.693413L78.608413 121.699910L176.797878 219.889374L37.935000 219.889374L37.935000 280.710626L176.797878 280.710626L78.608413 378.900090L121.614910 421.906587L219.804374 323.717122L219.804374 462.580000L280.625626 462.580000L280.625626 323.717122L378.815090 421.906587L421.821587 378.900090L323.632122 280.710626L462.495000 280.710626L462.495000 219.889374Z';
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 44 44"><path fill="${colour}" transform="translate(22 22) scale(${scale}) translate(-250.215 -250.52)" d="${path}"/></svg>`;
    document.body.style.setProperty('--ak-frame-circle-image', `url("data:image/svg+xml,${encodeURIComponent(svg)}")`, 'important');
  };
  const apply = (theme, persist = false) => {
    root.dataset.akTheme = validTheme(theme);
    if (persist) {
      try { localStorage.setItem(storageKey, root.dataset.akTheme); } catch (_) {}
    }
    syncButtons();
    syncGrainPattern();
    syncForegroundCorners();
    window.dispatchEvent(new CustomEvent('ak:colour-theme-change'));
  };
  // Restore before the first paint, without reloading or touching page flow.
  apply(readSaved());
  const mount = () => {
    if (document.querySelector('.ak-colour-theme-corner')) return;
    ['top-left'].forEach(corner => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ak-colour-theme-corner';
      button.dataset.corner = corner;
      button.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        apply(nextTheme(), true);
      });
      buttons.push(button);
      document.body.appendChild(button);
    });
    syncButtons();
    syncGrainPattern();
    syncForegroundCorners();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
  else mount();
  window.addEventListener('resize', syncForegroundCorners, { passive: true });
  // Moving between monitors can change DPR without changing innerWidth.
  // Re-arm the resolution query after each change; no scroll listener.
  let resolutionQuery;
  const watchResolution = () => {
    if (resolutionQuery) resolutionQuery.removeEventListener('change', resolutionChanged);
    resolutionQuery = window.matchMedia(`(resolution: ${pixels.ratio()}dppx)`);
    resolutionQuery.addEventListener('change', resolutionChanged);
  };
  const resolutionChanged = () => {
    watchResolution();
    syncGrainPattern();
    window.dispatchEvent(new CustomEvent('ak:pixel-ratio-change'));
  };
  watchResolution();
  window.addEventListener('pageshow', () => apply(readSaved()));
  window.addEventListener('storage', event => {
    if (event.key === storageKey || event.key === null) apply(readSaved());
  });
})();
